import useTranslation from 'next-translate/useTranslation';
import { memo, useState } from 'react';

import { Box, ButtonRadioGroup, Grid, Typography } from '@core/component';
import { Translate } from '@core/constant';
import { useContextCart, useContextMediaQuery, useContextUtil } from '@core/context';
import { useQueryApi } from '@core/hook';
import { EActionAttribute, EColor, ECountry, ESize, ETypographyVariant } from '@core/type';
import {
  DeliveryCountriesEntity,
  EDeliveryType,
  EQueryKey,
  ForecastedDeliveryEntity,
  ForecastedDeliveryRequestEntity,
  ProductBlockEntity,
} from '@core/type/api';
import { SanityMedia } from '@core/type/sanity';
import { ProductAlertActions, ProductCarousel } from '@core/widget';

import { ProductBudgetAndCartActions } from './ProductBudgetAndCartActions';
import { ProductOutOfStockBlock } from './ProductOutOfStockBlock';
import { RadioDeliveredToCountry } from './RadioDeliveredToCountry';
import { RadioStoreInVault } from './RadioStoreInVault';

const SectionProductBlockContainer = ({
  deliveryCountries,
  forecastedDeliveryData: forecastedDeliveryDataDefault,
  ...restProductProps
}: Omit<ProductBlockEntity, 'medias'> & {
  medias: SanityMedia[];
} & DeliveryCountriesEntity & {
    forecastedDeliveryData: ForecastedDeliveryEntity;
  }) => {
  const { currentStock, preorder } = restProductProps;
  const { userLocation } = useContextUtil();
  const { t: tProductShow } = useTranslation(Translate.page.PRODUCT_SHOW);

  const defaultSelectCountry = deliveryCountries?.some(
    ({ countryIso }) => countryIso === userLocation,
  )
    ? userLocation
    : ECountry.SWITZERLAND;

  const { isMobile, mediaQuery, isDesktop } = useContextMediaQuery();
  const [currentCountry, setCurrentCountry] = useState<ECountry>(defaultSelectCountry);
  const [isAutoSavingActive, setIsAutoSavingActive] = useState<boolean>(false);
  const [logisticType, setLogisticType] = useState<EDeliveryType>(EDeliveryType.STORAGE);

  const isProductInStock = (currentStock || preorder.preorderStock) > 0;

  const [productCount, setProductCount] = useState<number>(1);

  const { cart, isReady } = useContextCart();

  const { data: forecastedDeliveryData, isLoading } = useQueryApi<
    ForecastedDeliveryEntity,
    ForecastedDeliveryRequestEntity
  >(
    EQueryKey.FORECASTED_DELIVERY,
    {
      existingProducts: cart.products,
      newProduct: { id: restProductProps.id, quantity: productCount },
      countryIso: currentCountry,
    },
    {
      enabled: Boolean(isReady && restProductProps.id && productCount > 0),
      keepPreviousData: true,
    },
  );
  return (
    <Box
      as={EActionAttribute.SECTION}
      marginTop={isMobile ? '0' : '48px'}
      marginBottom={isMobile ? '64px' : '104px'}
    >
      <Grid isContainer columnSpacing={30}>
        <Grid lg={6}>
          <Box
            position={isDesktop ? 'sticky' : 'static'}
            top={mediaQuery === ESize.LG ? '270px' : '186px'}
            marginBottom={'20px'}
          >
            <ProductCarousel {...restProductProps} />
          </Box>
        </Grid>

        <Grid lg={6}>
          <Typography as={EActionAttribute.H1} variant={ETypographyVariant.H4} marginBottom="16px">
            {restProductProps.name}
          </Typography>
          <ProductAlertActions {...restProductProps} />
          {isProductInStock && restProductProps.isEnabled ? (
            <Box
              marginTop={isMobile ? '14px' : '0'}
              display="flex"
              flexDirection="column"
              alignItems={isMobile ? 'center' : undefined}
            >
              <ButtonRadioGroup
                value={logisticType}
                name="radiogroup"
                gap={16}
                onChange={({ target: { value } }) => setLogisticType(value as EDeliveryType)}
              >
                <RadioStoreInVault
                  isActive={logisticType === EDeliveryType.STORAGE}
                  product={restProductProps}
                  productCount={productCount}
                  value={EDeliveryType.STORAGE}
                  forecastedDeliveryData={forecastedDeliveryData || forecastedDeliveryDataDefault}
                  isForecastedDataLoading={isLoading}
                />
                <RadioDeliveredToCountry
                  isActive={logisticType === EDeliveryType.SHIPPING}
                  value={EDeliveryType.SHIPPING}
                  forecastedDeliveryData={forecastedDeliveryData || forecastedDeliveryDataDefault}
                  product={restProductProps}
                  productCount={productCount}
                  selectProps={{
                    value: currentCountry,
                    linePosition: deliveryCountries.filter(({ position }) => position).length,
                    options:
                      deliveryCountries?.map(({ countryIso, name }) => ({
                        value: countryIso,
                        label: name,
                      })) || [],
                    onChange: ({ target: { value } }) => setCurrentCountry(value as ECountry),
                  }}
                  isForecastedDataLoading={isLoading}
                />
              </ButtonRadioGroup>
              <ProductBudgetAndCartActions
                country={currentCountry}
                forecastedDelivery={forecastedDeliveryData || forecastedDeliveryDataDefault}
                isAutoSavingActive={isAutoSavingActive}
                setIsAutoSavingActive={setIsAutoSavingActive}
                logisticType={logisticType}
                product={restProductProps}
                productCount={productCount}
                setProductCount={setProductCount}
              />
            </Box>
          ) : !restProductProps.isEnabled ? (
            <Typography color={EColor.SECONDARY} marginTop={'40px'} variant={ETypographyVariant.H5}>
              {tProductShow('productNotAvailable')}
            </Typography>
          ) : (
            <ProductOutOfStockBlock product={restProductProps} />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export const SectionProductBlock = memo(SectionProductBlockContainer);
